import { useState, useContext } from "react";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import {
  Box,
  Typography,
  Link as MuiLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  OutlinedInput,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { useTranslation } from "react-i18next";

import { auth } from "../config/FirebaseConfig";
import UserApi from "../services/UserApi";
import CompanyApi from "../services/CompanyApi";
import SubscriptionApi from "../services/SubscriptionApi";
import { UserContext } from "../context/UserContext";
import PageContainer from "../components/page-container/PageContainer";
import { FormTextField, FormPasswordField, FormSubmitButton } from "../components/form";
import AppSnackbar from "../components/snackbar/AppSnackbar";
import { useFormValidation, validators } from "../hooks/useFormValidation";
import { getErrorMessage } from "../utils/errorHandler";
import { isStrongPassword } from "../utils/passwordValidator";
import { FORM_CONTAINER_SX } from "../theme/formStyles";

export default function SignUpPage() {
  const { t } = useTranslation();
  const userApi = new UserApi();
  const companyApi = new CompanyApi();
  const subscriptionApi = new SubscriptionApi();
  const { refreshUser } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const selectedTier = searchParams.get("tier") || "basic";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [userBody, setUserBody] = useState({ name: "", phone: "" });
  const [companyBody, setCompanyBody] = useState({ name: "" });
  const [pricingTier, setPricingTier] = useState(selectedTier);
  const [accountType, setAccountType] = useState("individual");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const navigate = useNavigate();

  const {
    handleBlur,
    clearFieldError,
    validateForm,
    getFieldError,
  } = useFormValidation();

  // Validation rules
  const validationRules = {
    name: [validators.required],
    email: [validators.required, validators.email],
    password: [validators.required, validators.minLength(8)],
    confirmPassword: [validators.required, validators.matches("password", "Password")],
    companyName: accountType === "organisation" ? [validators.required] : [],
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleSignUp = async (e) => {
    e.preventDefault();

    // Validate form
    const formData = {
      name: userBody.name,
      email,
      password,
      confirmPassword,
      companyName: companyBody.name,
    };

    if (!validateForm(formData, validationRules)) {
      return;
    }

    // Check password strength
    if (!isStrongPassword(password)) {
      setSnackbar({
        open: true,
        message: t("validation.password_not_strong"),
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: userBody?.name });

      await userApi.createUser(userCredential.user.uid, {
        uid: userCredential.user.uid,
        name: userBody.name,
        email,
        phone: userBody.phone,
        role: 'producer',
      });

      let companyId = null;

      if (accountType === "organisation") {
        const companyResponse = await companyApi.createCompany({
          name: companyBody.name,
          ownerId: userCredential.user.uid,
        });
        await userApi.updateUser(userCredential.user.uid, {
          companyId: companyResponse.companyId,
        });
        companyId = companyResponse.companyId;
      }

      await subscriptionApi.createSubscription({
        userId: userCredential.user.uid,
        companyId: companyId,
        planType: pricingTier,
      });

      refreshUser();
      navigate("/project");
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t),
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Box
        component="form"
        onSubmit={handleSignUp}
        sx={FORM_CONTAINER_SX}
      >
        <Typography variant="h3" align="center" gutterBottom>
          {t("auth.sign_up")}
        </Typography>

        <FormTextField
          name="name"
          label={t("auth.full_name")}
          value={userBody.name}
          onChange={(e) => {
            setUserBody({ ...userBody, name: e.target.value });
            clearFieldError("name");
          }}
          onBlur={() => handleBlur("name", userBody.name, validationRules.name)}
          error={getFieldError("name", t)}
          required
        />

        <FormTextField
          name="email"
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError("email");
          }}
          onBlur={() => handleBlur("email", email, validationRules.email)}
          error={getFieldError("email", t)}
          required
        />

        <FormPasswordField
          name="password"
          label={t("auth.password")}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearFieldError("password");
          }}
          onBlur={() => handleBlur("password", password, validationRules.password, { password })}
          error={getFieldError("password", t)}
          showStrengthMeter
          required
        />

        <FormPasswordField
          name="confirmPassword"
          label={t("auth.confirm_password")}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            clearFieldError("confirmPassword");
          }}
          onBlur={() => handleBlur("confirmPassword", confirmPassword, validationRules.confirmPassword, { password, confirmPassword })}
          error={getFieldError("confirmPassword", t) || (confirmPassword && confirmPassword !== password ? t("validation.passwords_must_match") : null)}
          required
        />

        <Box sx={{ mt: 2 }}>
          <InputLabel htmlFor="phone-number" sx={{ mb: 1, ml: '12px' }}>
            {t("auth.phone")}
          </InputLabel>
          <PhoneInput
            country={'za'}
            value={userBody.phone}
            onChange={(phone) => setUserBody({ ...userBody, phone: phone })}
            inputComponent={OutlinedInput}
            inputStyle={{ width: '100%', borderRadius: 16, backgroundColor: 'transparent', color: 'inherit' }}
            specialLabel=""
            required
            inputProps={{
              id: 'phone-number',
              variant: 'outlined',
              'aria-label': 'Phone Number',
            }}
          />
        </Box>

        <FormControl fullWidth margin="normal">
          <InputLabel id="pricing-tier-label">{t("auth.pricing_tier")}</InputLabel>
          <Select
            labelId="pricing-tier-label"
            value={pricingTier}
            label={t("auth.pricing_tier")}
            onChange={(e) => setPricingTier(e.target.value)}
          >
            <MenuItem value="basic">Basic (Beta)</MenuItem>
            <MenuItem value="pro" disabled>Pro</MenuItem>
            <MenuItem value="enterprise" disabled>Enterprise</MenuItem>
          </Select>
        </FormControl>

        <FormControl component="fieldset" margin="normal" fullWidth>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t("auth.account_type")}
          </Typography>
          <RadioGroup row value={accountType} onChange={(e) => setAccountType(e.target.value)}>
            <FormControlLabel value="individual" control={<Radio />} label={t("auth.individual")} />
            <FormControlLabel value="organisation" control={<Radio />} label={t("auth.organisation")} />
          </RadioGroup>
        </FormControl>

        {accountType === "organisation" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("auth.company_details")}
            </Typography>
            <FormTextField
              name="companyName"
              label={t("auth.company_name")}
              value={companyBody.name}
              onChange={(e) => {
                setCompanyBody({ ...companyBody, name: e.target.value });
                clearFieldError("companyName");
              }}
              onBlur={() => handleBlur("companyName", companyBody.name, validationRules.companyName)}
              error={getFieldError("companyName", t)}
              required
            />
          </Box>
        )}

        <FormSubmitButton
          label={t("auth.create_account")}
          loading={loading}
          sx={{ mt: 3 }}
        />

        <MuiLink
          component={RouterLink}
          to="/signin"
          variant="body2"
          display="block"
          align="center"
          sx={{ mt: 2 }}
        >
          {t("auth.have_account")}
        </MuiLink>
      </Box>

      <AppSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </PageContainer>
  );
}
